import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from "@mui/material/Box";
import MpgInput from "../../../../../CommonFormComp/MpgInput";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { v4 as uuidv4 } from 'uuid';
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import FormHelperText from '@mui/material/FormHelperText';
import sortBy from "lodash/sortBy";
import { useOutletContext } from "react-router-dom";

const AddFolderActionDialog = ({ open, setOpen, compositeFolderArrayInsideRoot, setCompositeFolderArrayInsideRoot }) => {

    const { loggedInUser } = useGetLoggedInUserDetails();
    const { emailAddress } = loggedInUser;

    useEffect(() => {
        if (!!open) {
            setOpen(true)
        } else {
            setOpen(false)
        }
    }, [open])

    const schema = yup.object().shape({
        folder_name: yup.string().label('This field').required().max(50),
    });

    const rhfFormInstanceForPDFolderAdd = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            folder_name: "",
        },
    });

    const { setValue, reset, setError, clearErrors, watch } = rhfFormInstanceForPDFolderAdd

    const inputFolderName = watch("folder_name")

    const handleClose = (event, reason) => {
        if (reason && reason == "backdropClick") {
            return '';
        } else {
            setOpen(false);
        }
        reset({ folder_name: "" })
    };

    const onSubmitForPDFolderAdd = (data, e) => {

        if (!!compositeFolderArrayInsideRoot && !!compositeFolderArrayInsideRoot.length) {

            /**sort folder array*/
            const inputArrayForSorting = [
                ...compositeFolderArrayInsideRoot,
                {
                    Id: uuidv4(),
                    /**adding this field to all newlyadded items to differntiate from existing one from api response */
                    IsNewlyAdded: true,

                    IsDeleted: false,
                    ResourceFolderName: data?.folder_name,
                    ProgramDescriptors: [],
                    ResourceSubFolders: [],
                    CreatedBy: emailAddress || ""
                }
            ] || []

            const sortedFolderArray = sortBy(inputArrayForSorting, function (item) {
                const folderName = item?.ResourceFolderName || ""
                /**we are converting all file names into smallcase for proper aphabetical order */
                return [folderName.toLowerCase()];
            });

            setCompositeFolderArrayInsideRoot(sortedFolderArray || [])
        } else {
            setCompositeFolderArrayInsideRoot([
                {
                    Id: uuidv4(),
                    /**adding this field to all newlyadded items to differntiate from existing one from api response */
                    IsNewlyAdded: true,

                    IsDeleted: false,
                    ResourceFolderName: data?.folder_name,
                    ProgramDescriptors: [],
                    ResourceSubFolders: [],
                    CreatedBy: emailAddress || ""
                }
            ])
        }
        handleClose()
    };

    /**folder name validation. at same level same name is not allowed. */
    let arrayOfOnlyFolderNames = []

    /**folder count validation. max 20 folders are allowed to create. */
    let folders = !!compositeFolderArrayInsideRoot && compositeFolderArrayInsideRoot.filter((eachFolder) => {
        if (!eachFolder?.IsDeleted) {
            arrayOfOnlyFolderNames.push(eachFolder?.ResourceFolderName)
            return eachFolder?.Id
        }
    })

    const [errorMsg, setErrorMsg] = useState("")
    useEffect(() => {
        /**folder name validation. at same level same name is not allowed. */
        if (!!arrayOfOnlyFolderNames.includes(inputFolderName?.trim())) {
            const foldersNameMsg = "This folder name exists at this level."
            setErrorMsg(foldersNameMsg)
        }
        /**folder count validation. max 20 folders are allowed to create. */
        else if (folders.length >= 20) {
            const foldersCountLimitMsg = "Max 20 folders could be created at this level."
            setErrorMsg(foldersCountLimitMsg)
        }
        else {
            setErrorMsg("")
        }
    }, [arrayOfOnlyFolderNames, inputFolderName, folders])

    /**disabling submit on failing custom validations */
    const [disableSubmitButton, setDisableSubmitButton] = useState(false)
    useEffect(() => {
        if (folders.length >= 20) {
            setDisableSubmitButton(true)
        } else if (!!arrayOfOnlyFolderNames.includes(inputFolderName?.trim())) {
            setDisableSubmitButton(true)
        } else {
            setDisableSubmitButton(false)
        }
    }, [folders, arrayOfOnlyFolderNames, inputFolderName])

    const resourcesCMSData = useOutletContext();

    const fetchClientAccResDtlsLabel = (key) => {
        if (resourcesCMSData && resourcesCMSData?.pageContent["ClientAccountResourcesDetails"]) {
          const resourcesCMSDataLabel = resourcesCMSData.pageContent["ClientAccountResourcesDetails"];
          return returnLabel(resourcesCMSDataLabel?.Labels, key)
        }
    }

    const fetchCommonCMSLabel = (key) => {
        if (resourcesCMSData && resourcesCMSData?.pageContent["CommonComponent"]) {
            const commonCMSDataLabel = resourcesCMSData.pageContent["CommonComponent"];
            return returnLabel(commonCMSDataLabel?.Labels, key)
        }
        return '';
    }

    const returnLabel = (labelArray, key) => {
        if (labelArray.length > 0 && key) {
            return labelArray.find((x) => x.Key === key)?.Phrase || '';
        }
        return '';
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={(e) => {
                    /**  e.stopPropagation(); required to use form within form seperately to avoid conflicts between nested form submits. */
                    e.stopPropagation();
                    rhfFormInstanceForPDFolderAdd.handleSubmit(onSubmitForPDFolderAdd)(e)
                }}
            >
                <DialogTitle>
                    {/* Create Folder */}
                    {fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.CreateFolder')}
                </DialogTitle>
                <DialogContent>
                    <MpgInput
                        rhfFormInstance={rhfFormInstanceForPDFolderAdd}
                        name={"folder_name"}
                        //label={"Folder Name"}
                        label={fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.FolderName')}
                        fullWidth
                    />
                    {
                        <FormHelperText error={!!errorMsg}>
                            {errorMsg}
                        </FormHelperText>
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            handleClose()
                        }}
                    >
                        {/* Cancel */}
                        {fetchCommonCMSLabel("Common.Cancel")}
                    </Button>

                    <Button
                        type="submit"
                        disabled={!!disableSubmitButton}
                    >
                        {/* OK */}
                        {fetchCommonCMSLabel("Common.OK")}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}
export default AddFolderActionDialog